import { capitalize as _capitalize, groupBy as _groupBy, sortBy as _sortBy, map as _map } from 'lodash-es';
import { Fragment } from 'react';
import Typography from '@mui/material/Typography';
import MailIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import MLink from '@mui/material/Link';
import { makeStyles } from 'tss-react/mui';
import type { Group } from '../../types/Group';
import type { GroupMember } from '../../types/GroupMember';
import { momentParse } from '../../utils/time';
import MemberRoleToggle from './member-role-toggle';
import MemberRemoveButton from './member-remove-button';
import MaterialLink from '../material-link';
import Blockquote from '../blockquote';
import HostUser from '../host-user';

const useStyles = makeStyles()((theme) => ({
  list: {
    padding: 0,
    margin: 0,
    listStyleType: 'none',
  },
  listItem: {
    margin: theme.spacing(1, 0),
  },
  buttons: {
    margin: theme.spacing(1, 0),
    '& > *:not(:first-of-type)': {
      marginLeft: theme.spacing(1),
    },
  },
  memberEmail: {
    margin: theme.spacing(0.5, 0),
  },
  memberPhone: {
    margin: theme.spacing(0.5, 0),
  },
  emailLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: '15px',
  },
  emailIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 15,
  },
  joinMessage: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.primary,
    fontSize: 12,
  },
  requestMessage: {
    margin: theme.spacing(0, 0, 1, 0),
  },
}));

type Props = {
  members?: GroupMember[];
  group?: Group;
};

const GroupMemberList = ({ members, group }: Props) => {
  const { classes } = useStyles();

  if (!members || !members.length) {
    return <p>No members</p>;
  }

  const membersByRole = _groupBy(
    _sortBy(members, (m) => ['REQUESTED', 'OWNER', 'MEMBER'].indexOf(m.role)),
    'role'
  );

  const roles = ['OWNER', 'MEMBER'];
  if (group.permissions.editable && membersByRole['REQUESTED']) {
    roles.unshift('REQUESTED');
  }

  return (
    <Fragment>
      {_map(roles, (role) => {
        const roleMembers = membersByRole[role] || [];
        const memberCount = roleMembers ? roleMembers.length : 0;
        const totalMemberCount = group.memberSummary[role] ?
          group.memberSummary[role].count :
          memberCount;
        const hiddenMemberCount = totalMemberCount - memberCount;
        return (
          <Fragment key={role}>
            <Typography variant="h3">{_capitalize(role)} ({totalMemberCount})</Typography>
            {totalMemberCount > 0 ? (
              <ul className={classes.list}>
                <Fragment>
                  {roleMembers.map((member) => {
                    const createdAt = momentParse(member.createdAt);
                    const approvedAt = member.approvedAt ? momentParse(member.approvedAt) : null;
                    let joinMessage;
                    if (approvedAt) {
                      joinMessage = (
                        <Fragment>
                          {' '}
                          via request to join
                          {member.approvedByUser && (
                            <Fragment>
                              {' – approved by '}
                              <MaterialLink href={`/people/${member.approvedByUser.username}`}>
                                {member.approvedByUser.displayName}
                              </MaterialLink>
                            </Fragment>
                          )}
                        </Fragment>
                      );
                    } else if (member.invite) {
                      joinMessage = ` via invite ${member.invite.inviteType.toLowerCase()}`;
                    }
                    return (
                      <li key={member.id} className={classes.listItem}>
                        <HostUser user={member.user} />
                        {group.permissions.editable && (
                          <Fragment>
                            {member.emailVisible && member.user.email && (
                              <div className={classes.memberEmail}>
                                <MLink href={`mailto:${member.user.email}`} className={classes.emailLink}>
                                  <MailIcon className={classes.emailIcon} />
                                  {member.user.email}
                                </MLink>
                              </div>
                            )}
                            {member.phoneNumberVisible && member.user.phoneNumberDisplay && (
                              <div className={classes.memberPhone}>
                                <MLink href={member.user.phoneNumberUri} className={classes.emailLink}>
                                  <PhoneIcon className={classes.emailIcon} />
                                  {member.user.phoneNumberDisplay}
                                </MLink>
                              </div>
                            )}
                            {member.role !== 'REQUESTED' && (
                              <div className={classes.joinMessage}>
                                <span>Joined {(approvedAt || createdAt).format('ll')}</span>
                                <span>{joinMessage}</span>
                              </div>
                            )}
                            {member.role === 'REQUESTED' && member.requestMessage && (
                              <Blockquote className={classes.requestMessage}>
                                {member.requestMessage}
                              </Blockquote>
                            )}
                            <div className={classes.buttons}>
                              <MemberRoleToggle member={member} />
                              <MemberRemoveButton member={member} group={group} />
                            </div>
                          </Fragment>
                        )}
                      </li>
                    );
                  })}
                </Fragment>
                {hiddenMemberCount > 0 && (
                  <li>{hiddenMemberCount} hidden</li>
                )}
              </ul>
            ) : (
              <p>None</p>
            )}
          </Fragment>
        )
      })}
    </Fragment>
  );
};

export default GroupMemberList;
